/**
 * Customer Login/Registration Feedback Handler
 * Manages success messages and visual feedback for login/registration
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    loginMessageDuration: 5000,
    welcomeMessageDuration: 60000,
    sessionStorageKey: 'login_just_happened',
    welcomeMessageKey: 'welcome_message_shown',
    newAccountParam: 'new_account'
  };

  /**
   * Show login success message temporarily
   */
  function showLoginMessage() {
    const loginBanner = document.querySelector('[data-login-message]');
    if (!loginBanner) return;

    // Check if this is a new account registration - if so, don't show login message
    const urlParams = new URLSearchParams(window.location.search);
    const isNewAccount = urlParams.get('new_account') === 'true';

    if (isNewAccount) {
      return;
    }

    // Check if user just logged in (not a page reload)
    const justLoggedIn = sessionStorage.getItem(CONFIG.sessionStorageKey);

    if (justLoggedIn === 'true') {
      loginBanner.classList.add('show');

      // Auto-hide after duration
      setTimeout(() => {
        loginBanner.classList.remove('show');
      }, CONFIG.loginMessageDuration);

      // Clear the flag
      sessionStorage.removeItem(CONFIG.sessionStorageKey);
    }
  }

  /**
   * Mark that user just logged in
   * Called from login page
   */
  function markLoginSuccess() {
    sessionStorage.setItem(CONFIG.sessionStorageKey, 'true');
  }

  // Flag to prevent multiple executions
  let welcomeHandled = false;

  /**
   * Handle new account registration success
   */
  function handleNewAccountWelcome() {
    if (welcomeHandled) {
      return;
    }
    welcomeHandled = true;

    const welcomeBanner = document.querySelector('.welcome-banner--success');

    if (!welcomeBanner) {
      return;
    }

    // Check URL parameter directly
    const urlParams = new URLSearchParams(window.location.search);
    const isNewAccount = urlParams.get('new_account') === 'true';

    if (isNewAccount) {
      // Check if message has already been shown (use localStorage for permanent persistence)
      const customerId = document.querySelector('[data-customer-id]')?.getAttribute('data-customer-id');
      const storageKey = `${CONFIG.welcomeMessageKey}_${customerId}`;
      const welcomeShown = localStorage.getItem(storageKey);


      if (!welcomeShown) {
        // Show welcome message for the first time
        // First display the block
        welcomeBanner.style.display = 'block';

        // Then trigger animation (after short delay for transition to work)
        setTimeout(() => {
          welcomeBanner.classList.add('show');
        }, 10);

        // Mark message as shown permanently
        localStorage.setItem(storageKey, 'true');

        // Start hiding after configured duration
        setTimeout(() => {
          welcomeBanner.classList.add('hide');
          welcomeBanner.classList.remove('show');

          // Remove completely from DOM after transition
          setTimeout(() => {
            welcomeBanner.style.display = 'none';
            welcomeBanner.classList.remove('hide');
          }, 500); // Wait for transition to finish (0.5s)
        }, CONFIG.welcomeMessageDuration);
      } else {
        // Message already shown, don't show again
        welcomeBanner.style.display = 'none';
      }

      // Remove the parameter from URL without page reload
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    } else {
      // Not a new account, hide the message
      welcomeBanner.style.display = 'none';
    }
  }

  /**
   * Add visual indicator to header account button
   */
  function enhanceHeaderAccountButton() {
    const accountButton = document.querySelector('.header-account-button--logged-in');
    if (!accountButton) return;

    // Add hover effect
    accountButton.addEventListener('mouseenter', function() {
      this.style.transform = 'translateY(-2px)';
    });

    accountButton.addEventListener('mouseleave', function() {
      this.style.transform = 'translateY(0)';
    });
  }

  /**
   * Initialize account dropdown functionality
   */
  function initAccountDropdown() {
    const dropdownWrapper = document.querySelector('[data-account-dropdown]');
    if (!dropdownWrapper) {
      return;
    }

    const toggleButton = dropdownWrapper.querySelector('[data-account-toggle]');
    const dropdownMenu = dropdownWrapper.querySelector('[data-account-menu]');

    if (!toggleButton || !dropdownMenu) {
      return;
    }


    let closeTimeout;

    // Show dropdown on hover
    dropdownWrapper.addEventListener('mouseenter', function() {
      clearTimeout(closeTimeout);
      openDropdown();
    });

    dropdownWrapper.addEventListener('mouseleave', function() {
      closeTimeout = setTimeout(function() {
        closeDropdown();
      }, 300); // Délai de 300ms avant de fermer
    });

    // Cancel close if hovering dropdown menu
    dropdownMenu.addEventListener('mouseenter', function() {
      clearTimeout(closeTimeout);
    });

    // Toggle dropdown on button click
    toggleButton.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();

      const isExpanded = this.getAttribute('aria-expanded') === 'true';

      if (isExpanded) {
        closeDropdown();
      } else {
        openDropdown();
      }
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', function(e) {
      if (!dropdownWrapper.contains(e.target)) {
        closeDropdown();
      }
    });

    // Close on Escape key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        closeDropdown();
      }
    });

    // Allow links to work normally
    dropdownMenu.querySelectorAll('a').forEach(function(link) {
      link.addEventListener('click', function(e) {
        // Let the link navigate normally, don't stop propagation
        // The browser will handle the navigation
      });
    });

    function openDropdown() {
      toggleButton.setAttribute('aria-expanded', 'true');
      dropdownMenu.setAttribute('aria-hidden', 'false');
    }

    function closeDropdown() {
      toggleButton.setAttribute('aria-expanded', 'false');
      dropdownMenu.setAttribute('aria-hidden', 'true');
    }

    // Return public methods for testing
    return {
      open: openDropdown,
      close: closeDropdown
    };
  }

  /**
   * Initialize on DOM ready
   */
  function init() {

    // Check if on account page
    if (document.body.classList.contains('template-customers-account')) {
      handleNewAccountWelcome();
      showLoginMessage();
    } else {
    }

    // Check if on login page and form submitted successfully
    if (document.body.classList.contains('template-customers-login')) {
      const loginForm = document.querySelector('form[action*="login"]');
      if (loginForm) {
        loginForm.addEventListener('submit', function() {
          // Mark that login attempt happened
          // Actual success will be determined by redirect to account page
          markLoginSuccess();
        });
      }
    }

    // Enhance header button
    enhanceHeaderAccountButton();

    // Initialize account dropdown
    initAccountDropdown();
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }


  // Export for testing
  window.CustomerLoginFeedback = {
    showLoginMessage,
    markLoginSuccess,
    handleNewAccountWelcome,
    CONFIG
  };

})();
